import React, { useState } from "react";
import { Field, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import { format, isValid, parse } from "date-fns";
import { FeatureFlagEnabled, LoadingIndicator, PolyIconButton } from "elements";
import {
  DeviceFragment,
  EntityConnectionFragment,
  useFirmwareUpdateInfoQuery,
  useNormalizedDeviceSettingsQuery
} from "generated/graphql";
import { infiniteNestLink, Link } from "polyrouter";
import { getSoftwareVersion } from "routes/Manage/Inventory/List/DeviceCard";
import { ProtectedButton, ProtectedLink } from "TsRoleGuards";
import i18n, { useTranslation } from "utilities/i18n";
import {
  isLensRelay,
  isPolyOS,
  deviceHasExtraOptions,
  isStudioUSB,
  isUcsDevice,
  isUsbDevice
} from "utilities/modelSelectors";
import { handleNullFields, compareVersion } from "utilities/stringUtils";
import { Layout } from "views/Main/Layouts";
import { useEntityConnections } from "./useEntityConnections";
import { useGetDesktopInfo } from "./useGetDesktopInfo";
import ConnectedEntity from "./ConnectedEntity";
import {
  SETTINGS as RELAY_SETTINGS,
  SETTINGS_PREFIX as RELAY_SETTINGS_PREFIX
} from "../Settings/RelayDeviceSettings";
import "./DeviceDetails.scss";
import {
  getProvisioningState,
  isProvisionedDevice,
  ProvisioningState
} from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import {
  DESKTOP_PROXY_AGENT,
  MIN_SOFTWARE_FOR_UTOPIA_OPTIONS
} from "Constants";
import { DeviceGroupInheritance } from "./DeviceGroupInheritance";
import { isMTRApp } from "utilities/roomUtils";

export type DetailProps = DeviceFragment;

export const Information: React.FC<DetailProps> = device => {
  const { t } = useTranslation();
  const {
    id,
    internalIp,
    externalIp,
    macAddress,
    serialNumber,
    activeApplicationName,
    hardwareModel,
    softwareVersion,
    softwareBuild
  } = device;

  const { data, fetching } = useFirmwareUpdateInfoQuery({
    variables: {
      id
    }
  });

  const updateAvailable = data?.firmwareUpdateInfo.isUpdateAvailable;
  const updateVersion = data?.firmwareUpdateInfo.updateVersion;

  let firstRow;

  firstRow = [
    {
      key: "serial-number",
      label: t("SERIAL_NUMBER"),
      content: <p>{handleNullFields(serialNumber)}</p>
    },
    {
      key: "mac-address",
      label: t("MAC_ADDRESS"),
      content: <p>{handleNullFields(macAddress)}</p>
    },
    {
      key: "internal-ip",
      label: t("IPV4"),
      content: <p>{handleNullFields(internalIp)}</p>
    },
    {
      key: "software-version",
      label: t("SOFTWARE_VERSION"),
      content: (
        <>
          <p className="m-0">
            {handleNullFields(
              getSoftwareVersion(softwareVersion, softwareBuild, hardwareModel)
            )}
          </p>
          {!fetching && updateAvailable && (
            <p className="latest-version">
              {t("UPDATE_AVAILABLE")} {updateVersion}
            </p>
          )}
        </>
      )
    },
    {
      key: "public-ip",
      label: t("PUBLIC_IP"),
      content: <p>{handleNullFields(externalIp)}</p>
    }
  ];

  if (isPolyOS(hardwareModel || "")) {
    firstRow.push({
      key: "provider",
      label: t("PROVIDER"),
      content: <p>{activeApplicationName ?? "-"}</p>
    });
  }

  const firstRowDisplay = makeColumnContentForCard(firstRow, 4);
  return <div className="card table-card details-card">{firstRowDisplay}</div>;
};

export const Location: React.FC<DetailProps> = ({ room, site }) => {
  const { t } = useTranslation();
  const location = [
    {
      key: "room-name",
      label: t("ROOM"),
      content: (
        <p data-testid="room-name">
          {room?.id && room?.name ? (
            <Link className="link" to={`/manage/rooms/details/${room.id}`}>
              {room.name}
            </Link>
          ) : (
            handleNullFields(room?.name)
          )}
        </p>
      )
    },
    {
      key: "site",
      label: t("SITE"),
      content: (
        <p data-testid="site-name">
          {site?.id && site?.name ? (
            <Link className="link" to={`/manage/sites/details/${site.id}`}>
              {site.name}
            </Link>
          ) : (
            handleNullFields(site?.name)
          )}
        </p>
      )
    }
  ];

  if (room?.floor) {
    location.push({
      key: "floor",
      label: t("FLOOR"),
      content: <p>{room?.floor}</p>
    });
  }

  if (site?.name && site?.address) {
    const address = site.address;
    location.push({
      key: "address",
      label: t("ADDRESS"),
      content: <p>{address.address1 || address.formattedAddress}</p>
    });
  }

  return (
    <div className="card table-card details-card">
      {makeColumnContentForCard(location, 4)}
    </div>
  );
};

export const RelayApplications: React.FC<DetailProps> = ({ id }) => {
  const { t } = useTranslation();
  const { data, fetching } = useNormalizedDeviceSettingsQuery({
    variables: { id }
  });
  const [isPasswordHidden, setPasswordHidden] = useState(true);

  const isPassword = (key: string) => key === RELAY_SETTINGS.PASSWORD;

  if (fetching) return <LoadingIndicator className="mb-15 inline" />;

  const fields = [
    { key: RELAY_SETTINGS.USERNAME, label: t("USERNAME") },
    { key: RELAY_SETTINGS.PASSWORD, label: t("PASSWORD") },
    { key: RELAY_SETTINGS.RPRM_HOST, label: t("RELAY_RPRM_HOST") }
  ].map(({ key, label }) => {
    const value = handleNullFields(
      data?.normalizedDeviceSettings.settings
        .filter(({ name }) => name.split(".")[0] === RELAY_SETTINGS_PREFIX)
        .map(({ name, value }) => ({ name: name.split(".")[1], value }))
        .find(({ name }) => name === key)?.value
    );
    return {
      key,
      label,
      content: isPassword(key) ? (
        <div className="d-flex">
          <p className={isPasswordHidden ? "password-hidden" : ""}>{value}</p>
          <PolyIconButton
            className="clickable mb-1 ml-5"
            data-testid="show-password"
            icon={isPasswordHidden ? "eye" : "eye_slash"}
            onClick={() => setPasswordHidden(!isPasswordHidden)}
          />
        </div>
      ) : (
        <p>{value}</p>
      )
    };
  });

  return (
    <>
      <label className="mb-1">{t("RELAY_RPRM_TITLE")}</label>
      <div className="card table-card details-card">
        {makeColumnContentForCard(fields, 4)}
      </div>
    </>
  );
};

export const HostInformation: React.FC<DetailProps> = ({
  connections,
  user
}) => {
  const { t } = useTranslation();
  const hostInformation = useGetDesktopInfo(connections);
  const date = hostInformation?.lastDetected?.split("T")[0];
  const lastDetected = date
    ? format(parse(date, "yyyy-MM-dd", new Date()), "MM-dd-yyyy")
    : null;
  const hostInfo = [
    {
      key: "host-name",
      label: t("DEVICE_USER"),
      content: <p>{handleNullFields(user?.name)}</p>
    },
    {
      key: "device",
      label: t("DEVICE"),
      content: (
        <p>
          {handleNullFields(hostInformation?.hardwareModel)}{" "}
          {handleNullFields(hostInformation?.hardwareRevision)}
        </p>
      )
    },
    {
      key: "lens-version",
      label: t("LENS_DESKTOP_VERSION"),
      content: <p>{handleNullFields(hostInformation?.softwareVersion)}</p>
    },
    {
      key: "last-detected",
      label: t("LAST_DETECTED"),
      content: <p>{handleNullFields(lastDetected)}</p>
    }
  ];

  return (
    <div className="card table-card details-card one-line-height">
      {makeColumnContentForCard(hostInfo, 4)}
    </div>
  );
};

export const formatDate = (date: string, dateFormat?: string) => {
  const newDate = new Date(date);

  if (!isValid(newDate)) {
    return "";
  }

  return format(newDate, dateFormat ?? "Pp").replace(",", "");
};

export function showLastDetected(
  fields: Field[],
  lastDetected: string,
  testId: string
): void {
  fields.push({
    key: "last-detected",
    label: i18n.t("LAST_DETECTED"),
    content: <p>{handleNullFields(formatDate(lastDetected, "P"))}</p>,
    "data-testid": `${testId}`
  });
}

/**
 * Needs to display fields for
 * @field Policy
 * @field Last provisioning touch
 * @field Device Registered
 * @field Device Last Seen
 * @param Field[] an array of fields which have key, label, and content (JSX)
 * @returns JSX
 */
export const ConnectivitySection: React.FC<DetailProps> = ({
  lastDetected,
  lastConfigRequestDate,
  connected,
  dateRegistered
}) => {
  const { t } = useTranslation();

  let formattedProvisionTouch: string = "-";
  let formattedDateRegistered: string = "-";
  const provisioningState = getProvisioningState(lastConfigRequestDate);
  if (
    provisioningState === ProvisioningState.ACTIVE ||
    provisioningState === ProvisioningState.INACTIVE
  ) {
    formattedProvisionTouch = formatDate(lastConfigRequestDate);
  }
  if (dateRegistered) {
    formattedDateRegistered = formatDate(dateRegistered);
  } else if (!dateRegistered) {
    formattedDateRegistered = t("REGISTER_DEVICE_PRIOR");
  }

  const fields: Field[] = [
    {
      key: "last-provisioning-touch",
      label: t("LAST_PROVISIONING_TOUCH"),
      content: <p>{formattedProvisionTouch}</p>
    },
    {
      key: "device-added",
      label: t("DEVICE_ADDED"),
      content: <p>{formattedDateRegistered}</p>
    }
  ];

  // only add in the lastDetected date if the device is not connected
  if (!connected && lastDetected) {
    showLastDetected(fields, lastDetected, "last-detected-connectivity");
  }

  const columnNumbers = connected ? 3 : 4;

  return (
    <div className="card table-card details-card">
      {makeColumnContentForCard(fields, columnNumbers)}
    </div>
  );
};

export const DeviceDetails: React.FC<{
  device: DetailProps;
  reset: boolean;
  restarted: boolean;
}> = ({ device, reset, restarted }) => {
  const { t } = useTranslation();
  const { Header } = Section;
  const { Content } = Layout;

  const isUsbDeviceModel = isUsbDevice(device?.hardwareModel || "");
  const isStudioUSBDevice = isStudioUSB(device?.hardwareModel || "");
  const isUcsDeviceModel = isUcsDevice(device?.hardwareModel || "");

  const isMinStudioUSBVersion = device
    ? compareVersion(device.softwareVersion, MIN_SOFTWARE_FOR_UTOPIA_OPTIONS) >=
      0
    : false;

  const isRelayDevice =
    device?.hardwareModel && isLensRelay(device.hardwareModel);
  const isDesktopDevice = device?.proxyAgent === DESKTOP_PROXY_AGENT;
  const isMTRAgent = isMTRApp(device);
  const isNotAnApp = !isDesktopDevice && !isMTRAgent;
  const linkedDevices = useEntityConnections(device?.entityConnections);
  const deviceHasOptions =
    isMTRAgent ||
    deviceHasExtraOptions(
      device?.supportsSettings,
      device?.supportsSoftwareUpdate,
      device?.provisioningEnabled
    );

  const dialOutAvailable = isUcsDeviceModel && !!device.connected;

  return (
    <>
      {device && (
        <div className="controls" data-testid="controls">
          {deviceHasOptions && (
            <>
              <ProtectedLink
                to={infiniteNestLink("/edit")}
                className="btn btn-secondary"
                requirement="update:device"
              >
                {t("EDIT_DEVICE")}
              </ProtectedLink>
              {device.connected && isNotAnApp && (
                <>
                  {restarted || reset ? (
                    <>
                      {(!isStudioUSBDevice || isMinStudioUSBVersion) && (
                        <ProtectedButton
                          className="btn btn-secondary disabled"
                          requirement="update:device"
                        >
                          {t("RESTART_DEVICE")}
                        </ProtectedButton>
                      )}
                      {!isUsbDeviceModel && !isRelayDevice && (
                        <ProtectedButton
                          className="btn btn-secondary disabled"
                          requirement="update:device"
                        >
                          {t("FACTORY_RESET_DEVICE")}
                        </ProtectedButton>
                      )}
                    </>
                  ) : (
                    <>
                      {(!isStudioUSBDevice || isMinStudioUSBVersion) && (
                        <ProtectedLink
                          to={infiniteNestLink("/restart")}
                          className="btn btn-secondary"
                          requirement="update:device"
                        >
                          {t("RESTART_DEVICE")}
                        </ProtectedLink>
                      )}
                      {!isUsbDeviceModel && !isRelayDevice && (
                        <ProtectedLink
                          to={infiniteNestLink("/reset")}
                          className="btn btn-secondary"
                          requirement="update:device"
                        >
                          {t("FACTORY_RESET_DEVICE")}
                        </ProtectedLink>
                      )}
                    </>
                  )}
                </>
              )}
              <FeatureFlagEnabled
                feature="CALL_CONTROL"
                andIf={dialOutAvailable}
              >
                <Link
                  to={infiniteNestLink("/dialOut")}
                  className="btn btn-secondary"
                >
                  {t("DIAL_OUT")}
                </Link>
              </FeatureFlagEnabled>
            </>
          )}
          <ProtectedLink
            to={infiniteNestLink("/delete")}
            className="btn mv-1"
            requirement="unregister:device"
          >
            {t("DELETE_DEVICE")}
          </ProtectedLink>
        </div>
      )}
      <Content.ScrollingList className="scroll-short">
        <Section>
          <Header>{t("DEVICE_INFORMATION")}</Header>
          <Information {...device} />
        </Section>
        <Section>
          <Header>{t("LOCATION_INFORMATION")}</Header>
          <Location {...device} />
        </Section>
        {deviceHasOptions && !isDesktopDevice && (
          <Section>
            <Header>{t("CONNECTIVITY")}</Header>
            <ConnectivitySection {...device} />
          </Section>
        )}
        {isRelayDevice && (
          <Section>
            <Header>{t("RELAY_APPLICATIONS")}</Header>
            <RelayApplications {...device} />
          </Section>
        )}
        {isDesktopDevice && (
          <>
            <Section>
              <Header>{t("DEVICE_USER_INFORMATION")}</Header>
              <HostInformation {...device} />
            </Section>
            {linkedDevices && linkedDevices.length > 0 && (
              <Section>
                <Header>{t("LINKED_DEVICES")}</Header>
                {linkedDevices.map(
                  (linkedDevice: EntityConnectionFragment, i: number) => (
                    <ConnectedEntity
                      {...linkedDevice}
                      data-testid={`Entity-${i}`}
                      key={linkedDevice.entityId}
                    />
                  )
                )}
              </Section>
            )}
          </>
        )}
        {!isDesktopDevice && linkedDevices && linkedDevices.length > 0 && (
          <Section>
            <Header>{t("LINKED_DEVICES")}</Header>
            {linkedDevices.map(
              (entity: EntityConnectionFragment, i: number) => (
                <ConnectedEntity
                  {...entity}
                  data-testid={`Entity-${i}`}
                  key={i}
                />
              )
            )}
          </Section>
        )}
        {device &&
        device.serialNumber &&
        isProvisionedDevice(
          device.hardwareModel,
          device.lastConfigRequestDate
        ) ? (
          <DeviceGroupInheritance {...device} />
        ) : null}
      </Content.ScrollingList>
    </>
  );
};
